/**
 * kHtmlSafeAllowList - the static allow-list data of kHtmlSafeCore (tags, attributes, value kinds, SVG content model,
 * denied parts, the SVG namespace).
 *
 * A LEAF of the safe-HTML family (2026-10-22): kHtmlSafeWalk and kHtmlSafeValueCore read it without importing
 * kHtmlSafeCore at runtime, which removed the import cycles between them. kHtmlSafeCore re-exports the class under
 * the same name, so an import of kHtmlSafeAllowList from kHtmlSafeCore is unchanged.
 */

/** The allow-list data behind kHtmlSafeCore.AiAnswerPolicy. Static literal tables, so fields. */
export class kHtmlSafeAllowList {
    /** The SVG namespace - the one value an "svgns" attribute may hold (kHtmlSafeCore.SvgNs is the same value). */
    public static readonly SvgNs: string = "http://www.w3.org/2000/svg";
    public static AiHtmlTags: string[] = [
        "table", "caption", "thead", "tbody", "tfoot", "tr", "th", "td", "colgroup", "col", "p", "br",
        "strong", "em", "b", "i", "code", "span", "div", "ul", "ol", "li", "h3", "h4", "small", "sub", "sup",
    ];
    public static AiSvgTags: string[] = [
        "svg", "g", "path", "rect", "circle", "ellipse", "line", "polyline", "polygon", "text", "tspan",
        "title", "desc", "defs", "linearGradient", "stop", "clipPath",
    ];
    public static AiHtmlAttributes: Record<string, string> = {
        "title": "text", "class": "class", "colspan": "span", "rowspan": "span", "span": "span",
        "scope": "enum:row|col|rowgroup|colgroup", "start": "int",
    };
    public static AiSvgAttributes: Record<string, string> = {
        "viewBox": "viewbox", "preserveAspectRatio": "aspect", "xmlns": "svgns",
        "width": "length", "height": "length", "x": "lengths", "y": "lengths", "dx": "lengths", "dy": "lengths",
        "cx": "length", "cy": "length", "r": "length", "rx": "length", "ry": "length",
        "x1": "length", "y1": "length", "x2": "length", "y2": "length",
        "d": "path", "points": "points", "transform": "transform",
        "fill": "paint", "stroke": "paint", "stop-color": "color", "clip-path": "clipref",
        "stroke-width": "length", "stroke-dasharray": "dash", "stroke-dashoffset": "length",
        "stroke-linecap": "enum:butt|round|square", "stroke-linejoin": "enum:miter|round|bevel|miter-clip|arcs",
        "fill-rule": "enum:nonzero|evenodd", "clip-rule": "enum:nonzero|evenodd",
        "opacity": "unit", "fill-opacity": "unit", "stroke-opacity": "unit", "stop-opacity": "unit", "offset": "unit",
        "text-anchor": "enum:start|middle|end",
        "dominant-baseline": "enum:auto|middle|central|hanging|alphabetic|ideographic|mathematical|text-top|text-bottom|text-before-edge|text-after-edge",
        "font-size": "length", "font-style": "enum:normal|italic",
        "font-weight": "enum:normal|bold|bolder|lighter|100|200|300|400|500|600|700|800|900",
        "gradientUnits": "enum:userSpaceOnUse|objectBoundingBox", "clipPathUnits": "enum:userSpaceOnUse|objectBoundingBox",
        "id": "id", "class": "class", "aria-label": "text", "aria-hidden": "enum:true|false",
        "role": "enum:img|presentation|none|group|list|listitem|graphics-document|graphics-object|graphics-symbol",
    };
    public static AiAttributeOnly: Record<string, string[]> = {
        "colspan": ["th", "td"], "rowspan": ["th", "td"], "scope": ["th"], "start": ["ol"], "span": ["col", "colgroup"],
        "viewBox": ["svg"], "preserveAspectRatio": ["svg"], "xmlns": ["svg"], "d": ["path"], "points": ["polyline", "polygon"],
        "offset": ["stop"], "stop-color": ["stop"], "stop-opacity": ["stop"],
        "gradientUnits": ["linearGradient"], "clipPathUnits": ["clipPath"],
    };

    /** Tags NO policy can admit: executable, navigating, resource-loading or namespace-switching. Lower case. */
    public static DeniedTags: string[] = [
        "script", "style", "foreignobject", "use", "image", "img", "a", "iframe", "frame", "frameset", "object", "embed",
        "applet", "animate", "set", "animatemotion", "animatetransform", "animatecolor", "mpath", "discard", "feimage",
        "pattern", "marker", "filter", "link", "meta", "base", "form", "input", "button", "textarea", "select", "option",
        "math", "noscript", "template", "xmp", "plaintext", "noembed", "noframes", "handler", "listener", "cursor",
        "video", "audio", "source", "track", "canvas", "portal", "svg:script", "html", "head", "body",
    ];
    /** Attribute names NO policy can admit (lower case). Any name starting "on" or holding ':' is denied too. */
    public static DeniedAttributes: string[] = [
        "href", "src", "srcset", "srcdoc", "style", "action", "formaction", "background", "poster", "lowsrc",
        "dynsrc", "data", "codebase", "attributename", "values", "from", "to", "by", "begin", "is", "ping",
    ];
    /** Substrings that refuse any attribute value once it is decoded, stripped and lower-cased. */
    public static DeniedValueParts: string[] = [
        "javascript:", "vbscript:", "livescript:", "data:", "expression(", "-moz-binding", "behavior:", "@import",
    ];
    /** SVG content model: what each allowed SVG element may hold (see kHtmlSafeWalk.SvgChildAllowed). */
    public static SvgContent: Record<string, string> = {
        "svg": "container", "g": "container", "defs": "container", "clipPath": "container",
        "text": "text", "tspan": "text", "title": "textonly", "desc": "textonly",
        "linearGradient": "gradient", "stop": "empty",
        "path": "shape", "rect": "shape", "circle": "shape", "ellipse": "shape", "line": "shape",
        "polyline": "shape", "polygon": "shape",
    };
}
